import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
import { translate } from 'react-polyglot';
import { slide as BurgerMenu } from 'react-burger-menu';
import Media from 'react-media';
import classNames from 'classnames';
import Logo from '../Logo';
import {
  Dashboard,
  Diary,
  Menu,
  User,
  Delete,
  Learn,
  Discharge
} from '../icons';
import playIcon from '../../assets/icons/dashboard-play.svg';
import HeaderDropdown from '../HeaderDropdown';
import MenuBar from './MenuBar';
import MobileMenu from './MobileMenu';
import AccountMenu from './AccountMenu';
import LoginButton from './LoginButton';
import SignUpButton from './SignUpButton';
import AskListHeaderMenu from '../AskListHeaderMenu';
import LoginModal from '../LoginModal';
import Login from '../Login';
import SignUpModal from '../SignUpModal';
import SignUp from '../SignUp';
import ProfileEditorModal from '../ProfileEditorModal';
import ProfileSelectorModal from '../ProfileSelectorModal';
import ForgotPasswordModal from '../ForgotPasswordModal';
import InviteModal from '../InviteModal';
import BabyEditorModal from '../BabyEditorModal';
import BabyCreateModal from '../BabyCreateModal';
import LogoutWarningModal from '../Modals/LogoutWarningModal/LogoutWarningModal';
import Modal from '../Modal';
import QuestionCreateContainer from '../../containers/QuestionCreateContainer';
import ForgotPassword from '../ForgotPassword';
import profileIcons from '../icons/profileIcons';
import ActivityTracker from '../../containers/TrackerContainer';
import { DiaryForgotPinModal } from '../diary';
import RequestSentModal from '../RequestSentModal';

import './Header.css';

const Header = props => {
  const {
    isAuthenticated,
    isAdmin,
    isStaff,
    profile,
    handleOpenProfileEditorModal,
    onChangeProfileClick,
    handleLogout,
    menuItems,
    menuShow,
    menuOpen,
    menuClose,
    t,
    questions,
    fetchAskList,
    location,
    history,
    handleOpenLoginModal,
    handleOpenSignUpModal
  } = props;
  const [accountOpen, setAccountOpen] = useState(false);
  const [modalOpen, setmModalOpen] = useState(false);

  const openModal = e => {
    if (e && e.currentTarget) {
      // force menu to close
      e.currentTarget.blur();
    }

    setmModalOpen(true);
  };

  const closeModal = () => {
    setmModalOpen(false);
  };

  const renderAccountMenu = () => (
    <AccountMenu
      isAdmin={isAdmin}
      isStaff={isStaff}
      profile={profile}
      closeBurgerMenu={() => setAccountOpen(false)}
      handleOpenProfileEditorModal={handleOpenProfileEditorModal}
      onChangeProfileClick={onChangeProfileClick}
      handleLogout={handleLogout}
      t={t}
    />
  );

  return (
    <header
      className={`header ${
        isAuthenticated ? 'user-is-authenticated' : 'user-not-authenticated'
      }`}
    >
      <div className="header__primary">
        <div className="header-button header-button--menu">
          <BurgerMenu
            isOpen={menuShow}
            width="87.5%"
            menuClassName="burger-menu burger-menu--left"
            overlayClassName="burger-menu-overlay"
            className={classNames({
              'burger-menu--open': menuShow
            })}
            customCrossIcon={
              <Delete width="32" height="32" viewBox="0 0 32 32" />
            }
            customBurgerIcon={
              <Menu
                width="32"
                height="32"
                viewBox="0 0 32 32"
                label={t('nav.menu')}
              />
            }
            onStateChange={state => (state.isOpen ? menuOpen() : menuClose())}
          >
            <div className="header__menu-header">
              <NavLink
                exact
                to="/"
                activeClassName="active"
                onClick={() => menuClose()}
              >
                <Logo type="vertical" className="header__logo-image" />
              </NavLink>
            </div>
            {menuItems ? (
              <MobileMenu
                isAuthenticated={isAuthenticated}
                menuItems={menuItems}
                profile={profile}
                menuClose={menuClose}
                openModal={openModal}
                t={t}
                onChangeProfileClick={onChangeProfileClick}
                handleLogout={handleLogout}
              />
            ) : null}
          </BurgerMenu>
        </div>
        <div className="header__logo">
          <NavLink exact to="/" activeClassName="active">
            <div className="header__logo--desktop">
              <Logo className="header__logo-image" />
            </div>
          </NavLink>
        </div>
        {!isAuthenticated && (
          <div className="header__logo--mobile">
            <NavLink exact to="/" activeClassName="active">
              <Logo type="mobile" className="header__logo-image" />
            </NavLink>
          </div>
        )}

        {isAuthenticated && (
          <Fragment>
            <NavLink
              className="header-button header-button--discharge header-button__link"
              activeClassName="active"
              to="/discharge"
            >
              <Discharge
                label={t('nav.dischargeTitle')}
                width="32"
                height="32"
                viewBox="0 0 32 32"
              />
            </NavLink>

            <NavLink
              className="header-button header-button__link header-button--learn"
              activeClassName="active"
              to="/learn"
            >
              <Learn
                label={t('nav.learn')}
                width="32"
                height="32"
                viewBox="0 0 32 32"
              />
            </NavLink>

            {/* Display track first for mobile, but learn first for desktop */}
            <Media query="(max-width: 767px)">
              {matches =>
                matches ? (
                  <React.Fragment>
                    <div className="header-button header-button--tracker">
                      <ActivityTracker />
                    </div>

                    <NavLink
                      className="header-button header-button--videos header-button__link"
                      activeClassName="active"
                      to="/videos"
                    >
                      <img src={playIcon} className="play-icon" alt="" />
                      {t('nav.videos')}
                    </NavLink>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <NavLink
                      className="header-button header-button--videos header-button__link"
                      activeClassName="active"
                      to="/videos"
                    >
                      <img src={playIcon} className="play-icon" alt="" />
                      {t('nav.videos')}
                    </NavLink>

                    <div className="header-button header-button--tracker">
                      <ActivityTracker />
                    </div>
                  </React.Fragment>
                )}
            </Media>

            <NavLink
              to="/dashboard"
              className="header-button__link header-button header-button--dashboard"
              activeClassName="active"
            >
              <Dashboard
                height="32"
                width="32"
                viewBox="0 0 32 32"
                label={t('nav.dashboard')}
              />
            </NavLink>

            {/* If on desktop, show diary link */}
            <Media query="(max-width: 767px)">
              {matches =>
                !matches && (
                  <NavLink
                    to="/diary"
                    className="header-button__link header-button header-button--diary"
                    activeClassName="active"
                  >
                    <Diary
                      label={t('nav.diary')}
                      width="32"
                      height="32"
                      viewBox="0 0 32 32"
                    />
                  </NavLink>
                )
              }
            </Media>

            <div
              className={`header-button header-button--ask-list ${
                window.location.href.split('/')[
                  window.location.href.split('/').length - 1
                ] === 'questions'
                  ? 'active'
                  : ''
              }`}
            >
              <AskListHeaderMenu
                profile={profile}
                questions={questions}
                fetchAskList={fetchAskList}
              />
            </div>
          </Fragment>
        )}
        <div className="header-button header-button--account">
          {isAuthenticated ? (
            <div>
              <HeaderDropdown
                className="header-button--account-dropdown"
                displayArrow={false}
                containerWidth="280px"
                title={
                  <img
                    src={profileIcons[profile.icon]}
                    width="40"
                    height="40"
                    className="header-button__account-greeting-icon"
                    alt=""
                  />
                }
                align="right"
              >
                {renderAccountMenu()}
              </HeaderDropdown>
              <BurgerMenu
                tabIndex="-1"
                isOpen={accountOpen}
                burgerButtonClassName="header-button--account-burger"
                right
                width="87.5%"
                menuClassName="burger-menu burger-menu--right"
                overlayClassName="burger-menu-overlay"
                customCrossIcon={<Delete label="close" />}
                customBurgerIcon={<User label={t('nav.account')} />}
                onStateChange={state => setAccountOpen(state.isOpen)}
              >
                <div className="header__menu-header">
                  <Logo className="header__menu-logo" type="icon" />
                  <h4>{t('nav.account')}</h4>
                </div>
                {renderAccountMenu()}
              </BurgerMenu>
            </div>
          ) : (
            <div className="header-buttons">
              <SignUpButton
                location={location}
                handleOpenSignUpModal={handleOpenSignUpModal}
              />
              <LoginButton
                location={location}
                handleOpenLoginModal={handleOpenLoginModal}
              />
            </div>
          )}
        </div>
      </div>
      {menuItems ? (
        <div className="header__secondary">
          <MenuBar
            location={location}
            isAuthenticated={isAuthenticated}
            menuItems={menuItems}
          />
        </div>
      ) : null}
      <SignUpModal
        {...props.signUpProps}
        handleOpenLoginModal={props.handleOpenLoginModal}
        closeSignUpModal={props.closeSignUpModal}
        onRequestClose={props.handleCloseSignUpModal}
        isOpen={props.isSignUpModalOpen}
        onOpenRequestSent={props.handleOpenRequestSentModal}
        duplicatedEmailError={props.duplicatedEmailError}
      />

      <LoginModal
        {...props.loginProps}
        isOpen={props.isLoginModalOpen}
        onRequestClose={props.handleCloseLoginModal}
        history={history}
        onSelectProfile={props.handleSelectProfile}
        isLoadingProfiles={props.isLoadingProfiles}
        onEditProfile={props.handleOpenProfileEditorModal}
        onEnterManageMode={props.handleEnterProfileManageMode}
        onLeaveManageMode={props.handleLeaveProfileManageMode}
        isWelcomeMessageVisible={false}
        isManagingProfiles={props.isManagingProfiles}
        onChangeProfileClick={props.onChangeProfileClick}
        onOpenForgotPassword={props.handleOpenForgotPasswordModal}
        // location={location}
        // onSelectProfile={props.onSelectProfile}
        // isManagingProfiles={props.isManagingProfiles}
        // onRequestClose={props.handleCloseLoginModal}
        // isOpen={props.isLoginModalOpen}
        // handleOpenSignUpModal={props.handleOpenSignUpModal}
        // closeLoginModal={props.closeLoginModal}
        // isWelcomeMessageVisible
      />
      <RequestSentModal
        {...props.requestSentProps}
        onSubmit={props.handleRequestSent}
        onRequestClose={props.handleCloseRequestSentModal}
        isOpen={props.isRequestSentModalOpen}
      />
      <ForgotPasswordModal
        {...props.forgotPasswordProps}
        onSubmit={props.handleForgotPassword}
        onRequestClose={props.handleCloseForgotPasswordModal}
        isOpen={props.isForgotPasswordModalOpen}
        t={t}
      />
      <ProfileSelectorModal
        profiles={props.loginProps.profiles}
        onRequestClose={props.handleCloseProfileSelectorModal}
        onEditProfile={props.handleOpenProfileEditorModal}
        onEnterManageMode={props.handleEnterProfileManageMode}
        onLeaveManageMode={props.handleLeaveProfileManageMode}
        onSelectProfile={props.handleSelectProfile}
        isWelcomeMessageVisible={false}
        isOpen={props.isProfileSelectorModalOpen}
        isLoadingProfiles={props.isLoadingProfiles}
        isManagingProfiles={props.isManagingProfiles}
        onChangeProfileClick={props.onChangeProfileClick}
      />
      <ProfileEditorModal
        isButtonDisabled={props.isProfileEditorButtonDisabled}
        isEditingCurrentProfile={props.isEditingCurrentProfile}
        isErrorMessageVisible={props.isProfileEditorErrorMessageVisible}
        isOpen={props.isProfileEditorModalOpen}
        isLoading={props.isLoadingProfile}
        isPrimary={props.isPrimaryProfile}
        isRoleOtherSelected={props.isRoleOtherSelected}
        onDelete={props.handleDeleteProfile}
        onRequestClose={props.handleCloseProfileEditorModal}
        initialValues={props.editingProfile}
        roles={props.roles}
        t={t}
        enableReinitialize
      />
      <BabyEditorModal
        isOpen={!!props.editingBaby}
        onRequestClose={props.handleCloseBabyEditorModal}
        initialValues={props.editingBaby}
        onBabySaved={props.handleCloseBabyEditorModal}
      />
      <BabyCreateModal
        isOpen={props.isBabyCreateModalOpen}
        onRequestClose={props.handleCloseBabyCreateModal}
        onBabyCreated={props.handleCloseBabyCreateModal}
      />
      <InviteModal
        {...props.inviteProps}
        onSubmit={props.handleInvite}
        onRequestClose={props.handleCloseInviteModal}
        isOpen={props.isInviteModalOpen}
      />
      <DiaryForgotPinModal
        isOpen={props.isForgotPinModalOpen}
        isLoading={props.isForgotPinModalLoading}
        isSuccessMessageVisible={props.isForgotPinSuccessMessageVisible}
        isFailureMessageVisible={props.isForgotPinFailureMessageVisible}
        onRequestClose={props.handleCloseForgotPinModal}
        onSubmit={props.handleForgotPin}
      />
      {profile && (
        <Modal isOpen={modalOpen} onRequestClose={closeModal}>
          <QuestionCreateContainer onQuestionAdded={closeModal} />
        </Modal>
      )}
      <LogoutWarningModal history={history} />
    </header>
  );
};

Header.defaultProps = {
  profile: null,
  editingProfile: null,
  editingBaby: null,
  menuItems: null
};

/*
 * location prop is needed because of:
 * https://github.com/ReactTraining/react-router/blob/master/packages/react-router/docs/guides/blocked-updates.md
 */
Header.propTypes = {
  location: PropTypes.shape({}).isRequired,
  history: PropTypes.shape({}).isRequired,
  t: PropTypes.func.isRequired,
  onChangeProfileClick: PropTypes.func.isRequired,
  onInviteClick: PropTypes.func.isRequired,
  fetchAskList: PropTypes.func.isRequired,
  profile: PropTypes.shape({
    id: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    icon: PropTypes.string
  }),
  editingProfile: PropTypes.shape({
    id: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    icon: PropTypes.string
  }),
  editingBaby: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string
  }),
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      question: PropTypes.string,
      details: PropTypes.string,
      date: PropTypes.date,
      pathname: PropTypes.string,
      answered: PropTypes.bool
    })
  ).isRequired,
  roles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      other: PropTypes.bool
    })
  ).isRequired,
  menuItems: PropTypes.arrayOf(PropTypes.shape({})),
  menuShow: PropTypes.bool.isRequired,
  menuOpen: PropTypes.func.isRequired,
  menuClose: PropTypes.func.isRequired,
  isEditingCurrentProfile: PropTypes.bool.isRequired,
  isProfileEditorButtonDisabled: PropTypes.bool.isRequired,
  isSignUpModalOpen: PropTypes.bool.isRequired,
  isLoginModalOpen: PropTypes.bool.isRequired,
  isForgotPasswordModalOpen: PropTypes.bool.isRequired,
  isRequestSentModalOpen: PropTypes.bool.isRequired,
  isLoadingProfile: PropTypes.bool.isRequired,
  isLoadingProfiles: PropTypes.bool.isRequired,
  isProfileEditorModalOpen: PropTypes.bool.isRequired,
  isProfileEditorErrorMessageVisible: PropTypes.bool.isRequired,
  isProfileSelectorModalOpen: PropTypes.bool.isRequired,
  isRoleOtherSelected: PropTypes.bool.isRequired,
  isManagingProfiles: PropTypes.bool.isRequired,
  isPrimaryProfile: PropTypes.bool.isRequired,
  isInviteModalOpen: PropTypes.bool.isRequired,
  isBabyCreateModalOpen: PropTypes.bool.isRequired,
  isForgotPinModalLoading: PropTypes.bool.isRequired,
  isForgotPinModalOpen: PropTypes.bool.isRequired,
  isForgotPinFailureMessageVisible: PropTypes.bool.isRequired,
  isForgotPinSuccessMessageVisible: PropTypes.bool.isRequired,
  signUpProps: PropTypes.shape(SignUp.propTypes).isRequired,
  loginProps: PropTypes.shape(Login.propTypes).isRequired,
  forgotPasswordProps: PropTypes.shape(ForgotPassword.propTypes),
  inviteProps: PropTypes.shape(ForgotPassword.propTypes),
  handleCloseRequestSentModal: PropTypes.func.isRequired,
  handleCloseForgotPasswordModal: PropTypes.func.isRequired,
  handleCloseSignUpModal: PropTypes.func.isRequired,
  handleCloseLoginModal: PropTypes.func.isRequired,
  handleCloseProfileEditorModal: PropTypes.func.isRequired,
  handleCloseProfileSelectorModal: PropTypes.func.isRequired,
  handleCloseInviteModal: PropTypes.func.isRequired,
  handleForgotPassword: PropTypes.func.isRequired,
  handleOpenRequestSentModal: PropTypes.func.isRequired,
  handleOpenForgotPasswordModal: PropTypes.func.isRequired,
  handleOpenLoginModal: PropTypes.func.isRequired,
  handleOpenSignUpModal: PropTypes.func.isRequired,
  handleOpenProfileEditorModal: PropTypes.func.isRequired,
  handleSignUp: PropTypes.func,
  handleLogin: PropTypes.func.isRequired,
  handleLogout: PropTypes.func.isRequired,
  handleEnterProfileManageMode: PropTypes.func.isRequired,
  handleLeaveProfileManageMode: PropTypes.func.isRequired,
  handleSelectProfile: PropTypes.func.isRequired,
  handleSaveProfile: PropTypes.func,
  handleDeleteProfile: PropTypes.func.isRequired,
  handleInvite: PropTypes.func.isRequired,
  handleCloseBabyEditorModal: PropTypes.func.isRequired,
  handleCloseBabyCreateModal: PropTypes.func.isRequired,
  handleCloseForgotPinModal: PropTypes.func.isRequired,
  handleForgotPin: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  isStaff: PropTypes.bool.isRequired
};

export default translate()(Header);



// WEBPACK FOOTER //
// ./src/components/Header/Header.js